import { HubConnection } from '@microsoft/signalr';
import { debounce } from 'lodash';

import { BedBoardIntakeDto } from '@medone/medonehp-bedboard-client';

import { SignalRDefaultDebounceOptions, SignalRDefaultDebounceTimeout } from '../../Config';
import { fetchBedboardAssignableUsers } from '../../post-acute/features/users/slice';
import { isBedBoardKiosk } from '../../shared/common/helpers';
import { store } from '../../shared/store';
import { fetchHospitals } from '../features/bedboard/admin/slice.hospitals';
import { fetchIntakes, fetchIntakesByKioskId, setLastAssignedIntakeId } from '../features/bedboard/slice';

let onBedBoardReleaseSuccess: (...args: any[]) => void = null;
let onBedBoardIntakeAdded: (...args: any[]) => void = null;
let onBedboardIntakeDeleted: (...args: any[]) => void = null;
let onHospitalAdded: (...args: any[]) => void = null;
let onUserAdded: (...args: any[]) => void = null;

export const removeBedBoardHubConnection = (connection: HubConnection) => {
    if (onBedBoardReleaseSuccess) connection.off('BedBoardReleaseSuccess', onBedBoardReleaseSuccess);
    if (onBedBoardIntakeAdded) connection.off('BedBoardIntakeAdded', onBedBoardIntakeAdded);
    if (onBedboardIntakeDeleted) connection.off('BedboardIntakeDeleted', onBedboardIntakeDeleted);
    if (onHospitalAdded) connection.off('HospitalAdded', onHospitalAdded);
    if (onUserAdded) connection.off('UserAdded', onUserAdded);
};

export const initBedBoardHubConnection = (connection: HubConnection, groupName?: string) => {
    onBedBoardReleaseSuccess = debounce(
        (message: any) => {
            window.location.reload();
        },
        SignalRDefaultDebounceTimeout,
        SignalRDefaultDebounceOptions
    );

    onBedBoardIntakeAdded = async (dto: BedBoardIntakeDto) => {
        // Only send this update payload to the kiosk the intake is assigned to
        if (isBedBoardKiosk() && dto.hospitalKioskId != null && groupName != null && groupName.toLowerCase() === dto.hospitalKioskId.toLowerCase()) {
            await store.dispatch(fetchIntakesByKioskId(dto.hospitalKioskId));
        }

        // If on the bedboard dashboard, then we can just reload the intakes table
        if (window.location.pathname === '/') {
            // Only reload here if the incoming DTO was not just "assigned" in this browser instance.
            // Assigned intakes are handled with the fadeout logic
            const lastAssignedIntakeId = store.getState().bedBoard.lastAssignedIntakeId;
            if (dto.id === lastAssignedIntakeId) {
                store.dispatch(setLastAssignedIntakeId(null));
            } else {
                await store.dispatch(fetchIntakes(null, false));
            }
        }
    };

    onBedboardIntakeDeleted = async (dto: BedBoardIntakeDto) => {
        // Only send this update payload to the kiosk the intake is assigned to
        if (isBedBoardKiosk() && dto.hospitalKioskId != null && groupName != null && groupName.toLowerCase() === dto.hospitalKioskId.toLowerCase()) {
            await store.dispatch(fetchIntakesByKioskId(dto.hospitalKioskId));
        }

        // If on the bedboard dashboard, then we can just reload the intakes table
        if (window.location.pathname === '/') {
            await store.dispatch(fetchIntakes(null, false));
        }
    };

    onHospitalAdded = debounce(
        async () => {
            await store.dispatch(fetchHospitals());

            if (window.location.pathname === '/') {
                await store.dispatch(fetchIntakes(null, false));
            }
        },
        SignalRDefaultDebounceTimeout,
        SignalRDefaultDebounceOptions
    );

    onUserAdded = debounce(
        async () => {
            if (!isBedBoardKiosk()) {
                await store.dispatch(fetchBedboardAssignableUsers());
            }
        },
        SignalRDefaultDebounceTimeout,
        SignalRDefaultDebounceOptions
    );

    connection.on('BedBoardReleaseSuccess', onBedBoardReleaseSuccess);
    connection.on('BedBoardIntakeAdded', onBedBoardIntakeAdded);
    connection.on('BedboardIntakeDeleted', onBedboardIntakeDeleted);
    connection.on('HospitalAdded', onHospitalAdded);
    connection.on('UserAdded', onUserAdded);
};
